import { SettingsIcon } from '@diligentcorp/atlas-react-icons';
import { Button, Checkbox, useEventCallback } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ExtendedAutocomplete } from '../../../inputs/extended-autocomplete';
import { ContainerPopper } from '../../components';
import { useDataGridContext } from '../../data-grid-context';

export interface GridToolbarColumnsButtonProps {
  columns: GridColumns<GridValidRowModel>;
  getTogglableColumns?: (
    columns: GridColumns<GridValidRowModel>,
  ) => (string | undefined)[];
}

function defaultGetTogglableColumns(columns: GridColumns<GridValidRowModel>) {
  const togglableColumns = columns
    .filter((col) => col.type !== 'checkboxSelection')
    .map((col) => col.type);
  return togglableColumns;
}

export function GridToolbarColumnsButton(props: GridToolbarColumnsButtonProps) {
  const { columns, getTogglableColumns = defaultGetTogglableColumns } = props;

  const { t } = useTranslation('common');

  const dataGridContext = useDataGridContext();
  const { columnVisibilityModel } = dataGridContext;

  const visibleColumns = columns.filter(
    (c) => columnVisibilityModel[c.field] ?? true,
  );

  const toggleAllColumns = useEventCallback((isVisible: boolean) => {
    const currentModel = columnVisibilityModel;
    const newModel = { ...currentModel };
    const togglableColumns = getTogglableColumns
      ? getTogglableColumns(columns)
      : null;

    columns.forEach((col) => {
      if (
        col.hideable !== false &&
        (togglableColumns == null || togglableColumns.includes(col.type))
      ) {
        if (isVisible) {
          // delete the key from the model instead of setting it to `true`
          delete newModel[col.field];
        } else {
          newModel[col.field] = false;
        }
      }
    });
    dataGridContext.onColumnVisibilityModelChange(newModel);
  });

  return (
    <ContainerPopper
      renderTarget={(props) => (
        <Button
          startIcon={<SettingsIcon />}
          onClick={(event) => {
            event.stopPropagation();
            props.handleClick(event);
          }}
          onKeyDown={props.handleKeyDown}
          data-testid='data-grid-columns-button'
        >
          {t('dataGrid.columnConfig')}
        </Button>
      )}
      renderChildren={(props) => (
        <ExtendedAutocomplete
          open
          multiple
          options={columns.filter((c) => c.type !== 'checkboxSelection')}
          value={visibleColumns}
          openOnFocus
          onClear={() => {
            toggleAllColumns(false);
          }}
          onApply={() => {
            toggleAllColumns(true);
          }}
          onChange={(_, __, reason, details) => {
            if (!details?.option) return;
            if (reason === 'selectOption') {
              dataGridContext.onColumnVisibilityModelChange({
                ...dataGridContext.columnVisibilityModel,
                [details.option.field]: true,
              });
            } else if (reason === 'removeOption') {
              dataGridContext.onColumnVisibilityModelChange({
                ...dataGridContext.columnVisibilityModel,
                [details.option.field]: false,
              });
            }
          }}
          onClose={props.handleClose}
          getOptionLabel={(option) => option.headerName ?? option.field}
          isOptionEqualToValue={(option, value) => option.field === value.field}
          disableCloseOnSelect
          getOptionDisabled={(option) => option.hideable === false}
          TextFieldProps={{
            autoFocus: true,
            placeholder: t('dataGrid.searchColumns'),
          }}
          ClearButtonProps={{
            children: t('dataGrid.clearAllColumns'),
          }}
          ApplyButtonProps={{
            children: t('dataGrid.selectAllColumns'),
          }}
          renderCheckbox={(option, state) => (
            <Checkbox
              checked={state.checked}
              data-testid={`extended-autocomplete-checkbox-${option.field}`}
            />
          )}
        />
      )}
    />
  );
}
